import React, { useRef } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import './App.css'; // Import the CSS file for styling
export default function ThermalReceipt({ header, items = [], isCancelled = false, formatDate }) {
    const printRef = useRef(null);

    const fmt =
        formatDate ||
        ((d) => {
            if (!d) return '';
            const dt = new Date(d);
            return `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${dt.getFullYear()}`;
        });
    return (
        <>
            <style>{`
                /* ── Thermal-safe font stack ─────────────────────────────
                   OCR-B > Roboto Mono > Courier Prime > Courier New
                   All are monospaced with uniform stroke width — ideal for
                   thermal dots which can't render thin serifs or hairlines. */
                @import url('https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;500;700&family=Courier+Prime:wght@400;700&display=swap');

                *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

                /* ── Screen wrapper ── */
                .tr-wrap {
                    display: flex;
                    justify-content: center;
                    align-items: flex-start;
                    min-height: 100vh;
                    background: #c8c4be;
                    padding: 28px 16px 48px;
                }

                /* ── 80mm receipt card (≈302px at 96dpi) ── */
                .thermal-container {
                    width: 302px;
                    background: #fff;
                    padding: 14px 12px 18px;
                    box-shadow: 0 2px 6px rgba(0,0,0,.12), 0 12px 40px rgba(0,0,0,.2);
                    color: #000;
                    /* Roboto Mono renders crisply at small sizes on thermal heads */
                    
                }

                /* ── Header ── */
                .thermal-header {
                    text-align: center;
                    margin-bottom: 6px;
                }
                .thermal-header h3 {
                    font-family: 'Roboto Mono', monospace;
                    font-size: 18px;
                    font-weight: 700;
                    letter-spacing: 1.5px;
                    text-transform: uppercase;
                    line-height: 1.2;
                    margin-bottom: 4px;
                }
                .thermal-header p {
                    font-size: 15px;
                    font-weight: bold;
                    line-height: 1.5;
                    margin: 1px 0;
                    letter-spacing: 0.2px;
                }

                /* ── Dividers ── */
                .tr-dotted {
                    border: none;
                    border-top: 2px dotted #000;
                    margin: 7px 0;
                    width: 100%;
                }

                /* ── Cancelled stamp ── */
                .cancelled-stamp {
                    display: inline-block;
                    border: 2.5px solid #ff0000;
                    color: #ff0000;
                    padding: 4px 10px;
                    font-size: 13px;
                    font-weight: 700;
                    letter-spacing: 1.5px;
                    transform: rotate(-5deg);
                    margin-bottom: 8px;
                    text-transform: uppercase;
                }

                /* ── Order meta ── */
                .tr-meta {
                    font-size: 15px;
                    font-weight: 500;
                    line-height: 1.7;
                    letter-spacing: 0.2px;
                }
                .tr-meta p { margin: 0; }

                /* ── Items table ── */
                .thermal-table {
                    width: 100%;
                    border-collapse: collapse;
                }
                .thermal-table thead th {
                    font-size: 14px;
                    font-weight: 700;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                    border-bottom: 1.5px dashed #000;
                    padding: 4px 0 5px;
                    text-align: left;
                }
                .thermal-table thead th.tr-right { text-align: right; }

                .thermal-table tbody td {
                    font-size: 14px;
                    font-weight: 400;
                    padding: 4px 0;
                    vertical-align: top;
                    line-height: 1.4;
                    word-break: break-word;
                }
                .thermal-table tbody td.tr-right {
                    text-align: right;
                    font-weight: 700;
                    white-space: nowrap;
                }

                /* ── Cancelled notice ── */
                .tr-cancelled-note {
                    text-align: center;
                    font-size: 12px;
                    font-weight: 700;
                    color: red;
                    letter-spacing: 0.5px;
                    margin-top: 2px;
                }

                /* ══ PRINT STYLES ═══════════════════════════════════════
                   Thermal roll = 80mm wide, height = auto (continuous)   */
                @media print {
                    @page {
                        size: 80mm auto;
                        margin: 0;
                    }
                    html, body {
                        width: 80mm;
                        height: auto !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        background: #fff !important;
                        overflow: visible !important;
                    }
                    .tr-wrap {
                        display: block !important;
                        min-height: 0 !important;
                        height: auto !important;
                        width: 80mm !important;
                        background: none !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        align-items: unset !important;
                        justify-content: unset !important;
                    }
                    .thermal-container {
                        width: 80mm !important;
                        padding: 0 3mm 5mm !important;
                        margin: 0 !important;
                        box-shadow: none !important;
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    .thermal-header {
                        margin-top: 0 !important;
                        padding-top: 0 !important;
                    }
                    .thermal-header h3 {
                        margin-top: 0 !important;
                        padding-top: 0 !important;
                    }
                    /* Slightly larger on paper for thermal dot clarity */
                    .thermal-header h3      { font-size: 18px !important; }
                    .thermal-header p       { font-size: 17px !important;font-weight: bold !important; }
                    .tr-meta                { font-size: 17px !important;font-weight: bold !important; }
                    .thermal-table thead th { font-size: 17px !important;font-weight: bold !important; }
                    .thermal-table tbody td { font-size: 17px !important;font-weight: bold !important; }
                    .tr-dotted              { margin: 5px 0 !important; }
                }
            `}</style>

            <div className="p-4">
                {/* This is the SPECIFIC DIV we want to print */}
                <div ref={printRef} className="printable-content">
                    <div className="thermal-container" id="printable-area">
                        {/* ── Header ── */}
                        <div className="thermal-header">
                            {isCancelled && <div className="cancelled-stamp">Order Cancelled</div>}
                            <h3>Sanman Restaurant</h3>
                            <p>#212, S.C.Road, Sheshadripuram</p>
                            <p>Bangalore - 560020</p>
                        </div>

                        <hr className="tr-dotted" />

                        {/* ── Order Meta ── */}
                        <div className="tr-meta">
                            <p>Ord No : {header?.ord_no || header?.bill_no}</p>
                            <p>Ord Date : {fmt(header?.bill_date)}</p>
                            <p>Ord Time : {header?.bill_time}</p>
                            <p>Cust Name : {header?.customer_name || 'N/A'}</p>
                            <p>Del Date : {fmt(header?.delivery_date)}</p>
                            <p>Del Time : {header?.delivery_time}</p>
                        </div>

                        <hr className="tr-dotted" />

                        {/* ── Items ── */}
                        <table className="thermal-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '75%' }}>Item Name</th>
                                    <th className="tr-right" style={{ width: '25%' }}>
                                        Qty
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, idx) => (
                                    <tr key={idx}>
                                        <td>{item.product_name || item.ItemName}</td>
                                        <td className="tr-right">{item.quantity ?? item.Qty ?? '0'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <hr className="tr-dotted" style={{ borderTopStyle: 'solid', borderTopWidth: '1px' }} />

                        {/* ── Cancelled notice ── */}
                        {isCancelled && <p className="tr-cancelled-note">*** This order has been cancelled ***</p>}
                    </div>
                </div>
            </div>
        </>
    );
}
